import { useState } from "react";
import { FileDown, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtRp } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SectionCard } from "@/components/common/Cards";
import { downloadBlob } from "@/components/cc/ccUtils";

export function ReportsTab({ reports, onReload }) {
  const [del, setDel] = useState(null);

  const remove = async () => {
    const r = del;
    setDel(null);
    try {
      await api.delete(`/cc/reports/${r.id}`);
      toast.success(`Laporan ${r.number} dihapus, transaksinya bisa diajukan lagi`);
      onReload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <SectionCard title="Laporan reimburse" description="Semua PDF reimburse yang pernah dibuat. Unduh ulang kapan saja." testid="cc-reports">
      {reports === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-14 bg-[hsl(220_10%_11%)]" />)}</div>
      ) : reports.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground" data-testid="cc-reports-empty">
          <FileText className="h-6 w-6 mx-auto text-muted-foreground" />
          <p className="mt-2">Belum ada laporan. Buat dari tab Ringkasan & Reimburse.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cc-reports-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground border-b border-border">
                <th className="text-left px-3 py-2.5 font-medium">Nomor</th>
                <th className="text-left px-3 py-2.5 font-medium">Perusahaan</th>
                <th className="text-left px-3 py-2.5 font-medium">Periode</th>
                <th className="text-right px-3 py-2.5 font-medium">Transaksi</th>
                <th className="text-right px-3 py-2.5 font-medium">Total</th>
                <th className="text-left px-3 py-2.5 font-medium">Dibuat</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-[hsl(220_10%_9%)]" data-testid={`cc-report-${r.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="num text-primary">{r.number}</div>
                    {r.title && <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{r.title}</div>}
                  </td>
                  <td className="px-3 py-2.5">{r.company_name}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.period_label || "–"}</td>
                  <td className="px-3 py-2.5 text-right num">{r.count}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtRp(r.total)}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right">
                    <Button size="sm" variant="outline" onClick={() => downloadBlob(`/cc/reports/${r.id}/pdf`, `Reimburse-${r.number}.pdf`).catch((e) => toast.error(errMsg(e)))} data-testid={`cc-report-download-${r.id}`}>
                      <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 ml-1 text-muted-foreground hover:text-[hsl(0_72%_66%)]" onClick={() => setDel(r)} data-testid={`cc-report-delete-${r.id}`} aria-label="Hapus laporan">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus laporan {del?.number}?</AlertDialogTitle>
            <AlertDialogDescription>Tanda "sudah diajukan" pada transaksinya akan dilepas sehingga bisa dimasukkan ke laporan baru.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cc-report-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="cc-report-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}
